import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import GuaranteesStepNavigationView from './GuaranteesStepNavigationView.tsx';
import { guaranteesStepNavigationStyles } from './styles.ts';
import type {
  TGuaranteesStepNavigationStyle,
  TGuaranteesStepNavigatorProps,
} from './types.ts';
import { buildGuaranteesStepItems } from './utils.ts';

const STEP_NAVIGATION_FEEDBACK_DELAY_MS = 140;

const GuaranteesStepNavigator = <TKey extends string>({
  containerId,
  currentStepKey,
  items: stepConfig,
  pathByKey,
  navigateStateByKey,
  containerStyles,
  stepperStyles,
}: TGuaranteesStepNavigatorProps<TKey>) => {
  const navigate = useNavigate();
  const navigationFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingStepKey, setPendingStepKey] = useState<TKey | null>(null);

  const items = useMemo(
    () =>
      buildGuaranteesStepItems({
        currentStepKey,
        items: stepConfig,
        onStepClick: (stepKey) => {
          if (stepKey === currentStepKey) {
            return;
          }

          if (navigationFeedbackTimeoutRef.current) {
            clearTimeout(navigationFeedbackTimeoutRef.current);
          }

          setPendingStepKey(stepKey);
          navigationFeedbackTimeoutRef.current = setTimeout(() => {
            navigate(pathByKey[stepKey], {
              state: navigateStateByKey?.[stepKey],
            });
          }, STEP_NAVIGATION_FEEDBACK_DELAY_MS);
        },
      }).map((item) => ({
        ...item,
        isPending: item.key === pendingStepKey,
      })),
    [currentStepKey, navigate, navigateStateByKey, pathByKey, pendingStepKey, stepConfig]
  );
  const current = items.findIndex((item) => item.key === currentStepKey);
  const resolvedContainerStyles = useCallback<TGuaranteesStepNavigationStyle>(
    (theme) => ({
      ...guaranteesStepNavigationStyles.container(theme),
      ...containerStyles?.(theme),
    }),
    [containerStyles]
  );
  const resolvedStepperStyles = useCallback<TGuaranteesStepNavigationStyle>(
    (theme) => ({
      ...guaranteesStepNavigationStyles.stepper(theme),
      ...stepperStyles?.(theme),
    }),
    [stepperStyles]
  );

  useEffect(
    () => () => {
      if (navigationFeedbackTimeoutRef.current) {
        clearTimeout(navigationFeedbackTimeoutRef.current);
      }
    },
    []
  );

  return (
    <GuaranteesStepNavigationView
      current={current}
      items={items}
      containerId={containerId}
      containerStyles={resolvedContainerStyles}
      stepperStyles={resolvedStepperStyles}
    />
  );
};

export default GuaranteesStepNavigator;
